"use client";

import React, { useCallback, useEffect, useMemo } from "react";
import { SpinnerDotIcon, HeartHandshakeIcon } from "staysure-component-library";

import withBrand from "@/components/with-brand";
import { PRODUCT } from "@/constants";
import useWindowSize from "@/hooks/use-window-size/use-window-size";
import { RenewalLandingPageHeaderType } from "@/schema/renewal-page/page-content.schema";
import { useAppSelector } from "@/store/hook";
import { BrandKey } from "@/types/common-types";

type RenewalsHeaderBannerProps = {
  content: RenewalLandingPageHeaderType;
  brand?: BrandKey;
};

function RenewalsHeaderBanner({
  content,
  brand = "staysure",
}: Readonly<RenewalsHeaderBannerProps>) {
  const [isLoadingHeaderData, setIsLoadingHeaderData] = React.useState(true);
  const { quoteCompleted, attributes } = useAppSelector((state) => state.quote);
  const { paymentDetails, priceResult } = attributes;
  const windowSize = useWindowSize();

  // To check the status of quote complete
  useEffect(() => {
    if (quoteCompleted && quoteCompleted.completed) {
      setIsLoadingHeaderData(false);
    }
  }, [quoteCompleted]);

  const getBottomText = useCallback(() => {
    if (windowSize && windowSize > 904) {
      return quoteCompleted && quoteCompleted.completed
        ? content.bottomTextDesktopLoaded
        : content.bottomTextDesktopLoading;
    }
    if (windowSize && windowSize >= 600) {
      return quoteCompleted && quoteCompleted.completed
        ? content.bottomTextTabletLoading
        : content.bottomTextTabletLoaded;
    }
    return quoteCompleted && quoteCompleted.completed
      ? content.bottomTextMobileLoading
      : content.bottomTextMobileLoaded;
  }, [windowSize, quoteCompleted, content]);

  // Based on this will display loyalty discount
  const isEligibleForLoyalty = useCallback(() => {
    return priceResult?.renewalPolicyPackage?.loyaltyDiscountEligible;
  }, [priceResult]);

  const getRenewalPriceText = useMemo(() => {
    if (
      paymentDetails &&
      paymentDetails.paymentOptions[0].cardPayment.premiumCreditPayment[0].pcl
        .eligible
    ) {
      return (
        <>
          <span className="md:text-3xl text-xl md:leading-custom-size-xl leading-custom-size-l/xl font-header-font">
            £{paymentDetails.paymentOptions[0].cardPayment.card.price}{" "}
          </span>
          <span className="font-body-font text-xs leading-custom-size-sm font-[400]">
            or
          </span>{" "}
          <span className="md:text-3xl text-xl md:leading-custom-size-xl leading-custom-size-l/xl font-header-font">
            £
            {
              paymentDetails.paymentOptions[0].cardPayment
                .premiumCreditPayment[0].pcl.collectionAmount
            }{" "}
          </span>
          <br className="md:hidden" />
          <span className="font-body-font text-xs leading-custom-size-sm font-[600]">
            a month{PRODUCT === "avanti" && "*"}
          </span>{" "}
          <span className="font-body-font text-xs leading-custom-size-sm font-[400]">
            (total £
            {
              paymentDetails.paymentOptions[0].cardPayment
                .premiumCreditPayment[0].pcl.totalCost
            }
            )
          </span>
        </>
      );
    }

    return (
      <span className="md:text-3xl text-xl md:leading-custom-size-xl leading-custom-size-l/xl font-header-font">
        £{paymentDetails?.paymentOptions[0].cardPayment.card.price ?? 0.0}
      </span>
    );
  }, [paymentDetails]);

  const heartHandshakeIcon = useMemo(() => {
    return (
      <HeartHandshakeIcon
        width={24}
        height={24}
        fill="rgba(255, 216, 115, 1)"
      />
    );
  }, []);

  return (
    <div className="h-fit bg-dark-surface-accent-1-primary rounded-radius-xxl flex flex-col justify-start items-center self-stretch md:pb-spacing-xl pb-spacing-l px-spacing-l md:px-0">
      <div className="flex flex-col justify-start items-center lg:max-w-[774px] md:max-w-[512px] max-w-[264px] text-surface-neutral-option-1 ">
        <h1 className="md:text-3xl text-surface-neutral-option-1 md:leading-custom-size-xl text-xl leading-custom-size-l/xl font-header-font whitespace-break-spaces">
          {content.title}
        </h1>
        {!isLoadingHeaderData ? (
          <div className="flex flex-col justify-center items-center mt-spacing-xs lg:mt-spacing-xs text-center">
            <span className="mb-spacing-xs md:w-full w-[264px]">
              {getRenewalPriceText}
            </span>
            {isEligibleForLoyalty() && (
              <>
                {brand === "avanti" ? (
                  <div className="flex flex-col justify-start md:items-end items-center md:mt-[-4px] mb-[8px] md:hidden">
                    <p className="flex items-center justify-center md:text-s text-2xs font-header-font md:leading-custom-size-sm leading-custom-size-xs text-interactive-primary-hovered">
                      <span className="pr-spacing-xxs">
                        {heartHandshakeIcon}
                      </span>{" "}
                      Includes{" "}
                      {`£${priceResult?.renewalPolicyPackage?.loyaltySavingAmount.toFixed(
                        2
                      )}`}{" "}
                    </p>
                    <p className="md:text-s text-2xs font-header-font md:leading-custom-size-sm leading-custom-size-xs text-interactive-primary-hovered">
                      loyalty saving
                    </p>
                  </div>
                ) : null}

                <div
                  className={`flex flex-row justify-start md:items-end items-center md:mt-[-4px] mb-[8px] ${
                    brand === "avanti" ? "hidden md:flex" : ""
                  }`}
                >
                  <span className="pr-spacing-xxs">{heartHandshakeIcon}</span>{" "}
                  <p className="md:text-s text-2xs font-header-font md:leading-custom-size-sm leading-custom-size-xs text-interactive-primary-hovered">
                    Includes{" "}
                    {`£${priceResult?.renewalPolicyPackage?.loyaltySavingAmount.toFixed(
                      2
                    )}`}{" "}
                    loyalty saving
                  </p>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="mt-[33.5px] md:mb-[16px] md:mt-[16px] lg:mt-[16px] mb-[41.5px] lg:mb-[8px]">
            <SpinnerDotIcon width={48} height={48} />
          </div>
        )}
        <p
          className={`font-body-font font-normal text-center text-2xs leading-custom-size-lg ${
            brand === "avanti"
              ? "text-surface-neutral-option-1 "
              : "text-surface-accent-1-primary"
          }   whitespace-break-spaces`}
        >
          {getBottomText()}
        </p>
      </div>
    </div>
  );
}

export default withBrand(RenewalsHeaderBanner);
